// useApplyHighlights.ts
import { useEffect } from "react";

export type Feature = {
  key: string;
  tooltip?: string;
  position?: "top" | "right" | "bottom" | "left";
};
export function useApplyHighlights(features: Feature[]) {
  useEffect(() => {
    const observer = new MutationObserver(() => {
      features.forEach((feature) => {
        const elements = document.querySelectorAll<HTMLElement>(feature.key);
        elements.forEach((el) => {
          if (!el.dataset.__highlighted) {
            // Add badge
            if (!el.querySelector(".badge-new")) {
              const badge = document.createElement("span");
              badge.className = "badge-new";
              badge.innerText = "NEW";
              badge.style.marginLeft = "6px";
              el.appendChild(badge);
            }

            // Add tooltip
            if (feature.tooltip) {
              el.addEventListener("mouseenter", () =>
                showTooltip(el, feature.tooltip!, feature.position || "right")
              );
              el.addEventListener("mouseleave", () => hideTooltip());
            }

            el.dataset.__highlighted = "true";
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [features]);
}

// Tooltip helpers
let tooltipEl: HTMLDivElement | undefined;

function showTooltip(target: HTMLElement, content: string, preferred: string) {
  hideTooltip();

  tooltipEl = document.createElement("div");
  tooltipEl.className = "tooltip-feature";
  tooltipEl.textContent = content;
  document.body.appendChild(tooltipEl);

  const rect = target.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();
  const spacing = 8;
  let top = 0,
    left = 0;

  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  // Basic positioning (same logic as Stimulus)
  switch (preferred) {
    case "top":
      top = scrollY + rect.top - tooltipRect.height - spacing;
      left = scrollX + rect.left + (rect.width - tooltipRect.width) / 2;
      break;
    case "bottom":
      top = scrollY + rect.bottom + spacing;
      left = scrollX + rect.left + (rect.width - tooltipRect.width) / 2;
      break;
    case "left":
      top = scrollY + rect.top + (rect.height - tooltipRect.height) / 2;
      left = scrollX + rect.left - tooltipRect.width - spacing;
      break;
    default:
      top = scrollY + rect.top + (rect.height - tooltipRect.height) / 2;
      left = scrollX + rect.right + spacing;
      break;
  }

  tooltipEl.style.position = "absolute";
  tooltipEl.style.top = `${top}px`;
  tooltipEl.style.left = `${left}px`;
  tooltipEl.classList.add(preferred);
  requestAnimationFrame(() => tooltipEl?.classList.add("visible"));
}

function hideTooltip() {
  tooltipEl?.remove();
  tooltipEl = undefined;
}
